/**
 * Automatisches Nachladen einer Browser-Kachel nach einem VORUEBERGEHENDEN Netzfehler.
 *
 * Anlass (01.11.2026, 13:20:37–13:21:09Z): Rafaels Mac konnte partner.werking.tools eine halbe
 * Minute nicht aufloesen. Die Konto-Kacheln blieben danach dauerhaft auf ERR_NAME_NOT_RESOLVED
 * stehen, weil das BrowserPanel genau einmal nach 2 s nachlud — da war das Netz noch weg.
 * Rafael soll dafuer keinen Handgriff brauchen.
 *
 * Deshalb: bei Netzfehlern des Hauptrahmens mit Backoff (2, 5, 10, 30, 60 s, dann alle 60 s)
 * nachladen, solange die Kachel eingehaengt ist; sofort (entprellt), wenn der Browser wieder
 * online meldet oder das Fenster sichtbar wird. Bei verborgenem Dokument wird nicht geladen,
 * sondern bis zum Sichtbarwerden gewartet. Der Zustand wird ueber `anzeigen` sichtbar gemacht
 * (kein stilles Pollen). Reine Logik ohne DOM/React — Uhr und Wirkungen werden injiziert,
 * damit sich der Ablauf offline durchspielen laesst.
 */

/** Chromium-Netzfehler, nach denen ein spaeterer Versuch Erfolg haben kann. */
const VORUEBERGEHENDE_CODES: ReadonlySet<number> = new Set([
  -7, // TIMED_OUT
  -21, // NETWORK_CHANGED
  -100, // CONNECTION_CLOSED
  -101, // CONNECTION_RESET
  -102, // CONNECTION_REFUSED
  -104, // CONNECTION_FAILED
  -105, // NAME_NOT_RESOLVED
  -106, // INTERNET_DISCONNECTED
  -109, // ADDRESS_UNREACHABLE
  -118, // CONNECTION_TIMED_OUT
  -137, // NAME_RESOLUTION_FAILED
]);

/** Ein did-fail-load-Ereignis, nach dem automatisch nachgeladen werden soll? Unterrahmen und -3 (ABORTED) nie. */
export function istVoruebergehenderLadefehler(e: { errorCode?: number; isMainFrame?: boolean }): boolean {
  if (e.isMainFrame === false) return false;
  return typeof e.errorCode === 'number' && VORUEBERGEHENDE_CODES.has(e.errorCode);
}

export const NACHLADE_STUFEN_MS: readonly number[] = [2000, 5000, 10000, 30000, 60000];

/** Wartezeit vor dem Versuch Nr. `versuch` (0-basiert); ab der letzten Stufe gleichbleibend. */
export function nachladeVerzoegerung(versuch: number): number {
  const i = Math.max(0, Math.min(Math.floor(versuch), NACHLADE_STUFEN_MS.length - 1));
  return NACHLADE_STUFEN_MS[i];
}

export interface NachladeZustand {
  code: number;
  /** Anzahl bereits geplanter automatischer Versuche. */
  versuch: number;
  /** Zeitpunkt (uhr.jetzt()) des naechsten Versuchs; null = wartet auf Sichtbarkeit. */
  naechsterVersuchUm: number | null;
}

export interface NachladeUhr {
  jetzt(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
}

export interface NachladePlanerOptionen {
  uhr: NachladeUhr;
  /** Laedt die Kachel neu (z. B. webview.reload()). */
  nachladen: () => void;
  /** Sichtbarer Zustand; null = kein Netzfehler (mehr). */
  anzeigen: (z: NachladeZustand | null) => void;
  /** true = Dokument verborgen, dann nicht laden, sondern auf `sofort('sichtbar')` warten. */
  istVerborgen?: () => boolean;
  /** Mindestabstand zwischen zwei sofortigen Versuchen (online/sichtbar). */
  entprellMs?: number;
}

export interface NachladePlaner {
  /** did-fail-load des Hauptrahmens mit vorübergehendem Code: naechsten Versuch planen. */
  fehler(code: number): void;
  /** Ein Ladevorgang hat begonnen (eigener oder fremder) — geplanter Versuch ist ueberholt, Zaehler bleibt. */
  ladeStart(): void;
  /** Erfolgreich geladen: alles zuruecksetzen. */
  erfolg(): void;
  /** online/sichtbar/Knopf: sofort nachladen, wenn ein Netzfehler steht (entprellt, ausser `erzwingen`). */
  sofort(grund: 'online' | 'sichtbar' | 'knopf'): boolean;
  zustand(): NachladeZustand | null;
  /** Neue Adresse/Instanz oder nicht-voruebergehender Fehler: Zustand verwerfen (Anzeige null). */
  zuruecksetzen(): void;
  /** Unmount: Timer weg, danach wirkungslos. */
  abbauen(): void;
}

export function erzeugeNachladePlaner(o: NachladePlanerOptionen): NachladePlaner {
  const entprellMs = o.entprellMs ?? 3000;
  let z: NachladeZustand | null = null;
  let timer: unknown = null;
  let laedt = false;
  let letzterSofortVersuch = -Infinity;
  let abgebaut = false;

  const timerWeg = () => {
    if (timer !== null) { o.uhr.clearTimeout(timer); timer = null; }
  };
  const melden = () => o.anzeigen(z ? { ...z } : null);
  const laden = () => {
    timerWeg();
    laedt = true;
    o.nachladen();
  };
  const faellig = () => {
    timer = null;
    if (abgebaut || !z) return;
    if (o.istVerborgen?.()) {
      z = { ...z, naechsterVersuchUm: null };
      melden();
      return;
    }
    laden();
  };

  return {
    fehler(code) {
      if (abgebaut) return;
      laedt = false;
      timerWeg();
      const versuch = z ? z.versuch : 0;
      const ms = nachladeVerzoegerung(versuch);
      z = { code, versuch: versuch + 1, naechsterVersuchUm: o.uhr.jetzt() + ms };
      timer = o.uhr.setTimeout(faellig, ms);
      melden();
    },
    ladeStart() {
      if (abgebaut) return;
      laedt = true;
      timerWeg();
    },
    erfolg() {
      if (abgebaut) return;
      laedt = false;
      timerWeg();
      if (z) { z = null; melden(); }
    },
    sofort(grund) {
      if (abgebaut || !z) return false;
      if (grund !== 'knopf') {
        if (laedt) return false; // laeuft schon ein Versuch, keinen zweiten obendrauf
        if (o.istVerborgen?.()) return false;
        if (o.uhr.jetzt() - letzterSofortVersuch < entprellMs) return false;
      }
      letzterSofortVersuch = o.uhr.jetzt();
      laden();
      return true;
    },
    zustand: () => (z ? { ...z } : null),
    zuruecksetzen() {
      if (abgebaut) return;
      timerWeg();
      laedt = false;
      if (z) { z = null; melden(); }
    },
    abbauen() {
      abgebaut = true;
      timerWeg();
      z = null;
    },
  };
}
